import {isUndefined} from 'lodash';

import {isShallowEqual} from '../../helpers/reselectHelpers';
import {AppAction, AppActionsEnum} from '../appActions';
import {mergeResourceTables} from '../appTypes';
import {accessReducer} from './access/accessReducer';
import {accountsReducer} from './accounts/accountsReducer';
import {activitiesReducer} from './activities/activitiesReducer';
import {answerFoldersReducer} from './answerFolders/answerFolderReducer';
import {answersReducer} from './answers/answersReducer';
import {apiTokensReducer} from './apiTokens/apiTokensReducer';
import {applicationsReducer} from './applications/applicationsReducer';
import {legacyApplicationsReducer} from './applications/legacyApplicationsReducer';
import {calendarsReducer} from './calendar/calendarsReducer';
import {calendarEventsReducer} from './calendarEvents/calendarEventsReducer';
import {calendarEventVersionsReducer} from './calendarEventVersions/calendarEventVersionsReducer';
import {calendarVersionsReducer} from './calendarVersions/calendarVersionsReducer';
import {cardGroupsReducer} from './cardGroups/cardGroupsReducer';
import {cardNotesReducer} from './cardNotes/cardNotesReducer';
import {cardsReducer} from './cards/cardsReducer';
import {channelsReducer} from './channels/channelsReducer';
import {isChatUserOnlineAction} from './chat/chatUserOnlineActions';
import {chatUserOnlineReducer} from './chat/chatUserOnlineReducer';
import {isChatPrecogAction} from './chatPrecogs/chatPrecogsActions';
import {chatPrecogReducer} from './chatPrecogs/chatPrecogsReducer';
import {commentsReducer} from './comments/commentsReducer';
import {companyReducer} from './company/companyReducer';
import {contactsReducer} from './contacts/contactsReducer';
import {conversationsReducer} from './conversations/conversationsReducer';
import {countersReducer} from './counters/countersReducer';
import {crmSyncsReducer} from './crmSync/crmSyncReducer';
import {customEmojiReducer} from './customEmojis/customEmojiReducer';
import {customFieldsReducer} from './customFields/customFieldsReducer';
import {customViewsReducer} from './customViews/customViewsReducer';
import {DataActionsEnum} from './dataActions';
import {DataState, DataStateForBoot} from './dataState';
import {delegationsReducer} from './delegations/delegationsReducer';
import {externalChannelTypesReducer} from './externalChannelTypes/externalChannelTypesReducer';
import {inboxesReducer} from './inboxes/inboxesReducer';
import {integrationErrorsReducer} from './integrations/integrationErrorsReducer';
import {integrationsReducer} from './integrations/integrationsReducer';
import {integrationTypesReducer} from './integrationTypes/integrationTypesReducer';
import {jobsReducer} from './jobs/jobsReducer';
import {lensesReducer} from './lenses/lensesReducer';
import {localApplicationsReducer} from './localApplications/localApplicationsReducer';
import {localPreferencesReducer} from './localPreferences/localPreferencesReducer';
import {messagesReducer} from './messages/messagesReducer';
import {offlineDraftCommentUidsReducer, offlineDraftMessageUidsReducer} from './offline/offlineReducers';
import {pluginReducer} from './plugins/pluginsReducer';
import {isConversationPrecogAction} from './precogs/conversationPrecogsActions';
import {conversationPrecogReducer} from './precogs/conversationPrecogsReducer';
import {preferencesReducer} from './preferences/preferencesReducer';
import {rolesReducer} from './roles/rolesReducer';
import {schedulingLinksReducer} from './schedulingLinks/schedulingLinksReducer';
import {signaturesReducer} from './signatures/signaturesReducer';
import {tagsReducer} from './tags/tagsReducer';
import {isOnlineTeammatesAction} from './teammates/onlineTeammatesActions';
import {onlineTeammatesReducer} from './teammates/onlineTeammatesReducer';
import {teammatesReducer} from './teammates/teammatesReducer';
import {teamsReducer} from './teams/teamsReducer';
import {topicsReducer} from './topics/topicsReducer';
import {userMetadataReducer} from './userMetadata/userMetadataReducer';
import {vacationRespondersReducer} from './vacationResponders/vacationRespondersReducer';
import {workspaceReducer} from './workspace/workspaceReducer';

export function dataReducer(prevState: DataState, action: AppAction): DataState {
  // Precog optimization.
  if (isConversationPrecogAction(action)) {
    return {
      ...prevState,
      conversationPrecogs: conversationPrecogReducer(prevState.conversationPrecogs, prevState.userId, action),
    };
  }

  if (isChatPrecogAction(action)) {
    return {
      ...prevState,
      chatPrecogs: chatPrecogReducer(prevState.chatPrecogs, action),
    };
  }

  if (isChatUserOnlineAction(action)) {
    return {
      ...prevState,
      chatUsersOnline: chatUserOnlineReducer(prevState.chatUsersOnline, action),
    };
  }

  // Online teammates optimization.
  if (isOnlineTeammatesAction(action)) {
    return {...prevState, onlineTeammates: onlineTeammatesReducer(prevState.onlineTeammates, action)};
  }

  // If this is a boot action, update directly.
  if (action.type === DataActionsEnum.BOOT) {
    return {
      ...mergeDataForBoot(prevState, action.payload.data, action.payload.deltaSince),
      counters: countersReducer(prevState.counters, action),
    };
  }

  // If this is a GC action, update directly.
  if (action.type === AppActionsEnum.GC) {
    return action.payload.data;
  }

  const newInboxes = inboxesReducer(prevState.inboxes, action);
  const newChannels = channelsReducer(prevState.channels, action);
  const newConversations = conversationsReducer(prevState.userId, prevState.conversations, action);

  const nextState: DataState = {
    ...prevState,
    // Persistent values.
    access: accessReducer(prevState.access, action),
    localPreferences: localPreferencesReducer(prevState.localPreferences, action),
    preferences: preferencesReducer(prevState.preferences, action),
    userMetadata: userMetadataReducer(prevState.userMetadata, action),
    counters: countersReducer(prevState.counters, action),
    company: companyReducer(prevState.company, action),
    teams: teamsReducer(prevState.teams, action),
    teammates: teammatesReducer(prevState.teammates, action),
    lazyTeammates: teammatesReducer(prevState.lazyTeammates, action),
    delegations: delegationsReducer(prevState.delegations, action),
    answers: answersReducer(prevState.answers, action),
    answerFolders: answerFoldersReducer(prevState.answerFolders, action),
    signatures: signaturesReducer(prevState.signatures, action),
    integrations: integrationsReducer(prevState.integrations, action),
    integrationErrors: integrationErrorsReducer(prevState.integrationErrors, action),
    plugins: pluginReducer(prevState.plugins, action),
    legacyApplications: legacyApplicationsReducer(prevState.legacyApplications, action),
    customViews: customViewsReducer(prevState.customViews, action),
    customFields: customFieldsReducer(prevState.customFields, action),
    roles: rolesReducer(prevState.roles, action),
    jobs: jobsReducer(prevState.jobs, action),
    offlineDraftMessageUids: offlineDraftMessageUidsReducer(prevState.offlineDraftMessageUids, action),
    offlineDraftCommentUids: offlineDraftCommentUidsReducer(prevState.offlineDraftCommentUids, action),
    calendars: calendarsReducer(prevState.calendars, action),
    calendarVersions: calendarVersionsReducer(prevState.calendarVersions, action),
    customEmojis: customEmojiReducer(prevState.customEmojis, action),
    workspace: workspaceReducer(prevState.workspace, action),
    crmSyncs: crmSyncsReducer(prevState.crmSyncs, action),
    externalChannelTypes: externalChannelTypesReducer(prevState.externalChannelTypes, action),
    integrationTypes: integrationTypesReducer(prevState.integrationTypes, action),
    applications: applicationsReducer(prevState.applications, action),
    // Transient values.
    inboxes: newInboxes,
    channels: newChannels,
    contacts: contactsReducer(prevState.contacts, action),
    cards: cardsReducer(prevState.cards, action),
    cardGroups: cardGroupsReducer(prevState.cardGroups, action),
    cardNotes: cardNotesReducer(prevState.cardNotes, action),
    tags: tagsReducer(prevState.tags, action),
    lenses: lensesReducer(prevState.lenses, action),
    conversations: newConversations,
    conversationPrecogs: conversationPrecogReducer(prevState.conversationPrecogs, prevState.userId, action),
    messages: messagesReducer(prevState.messages, action),
    comments: commentsReducer(prevState.comments, action),
    activities: activitiesReducer(prevState.activities, action),
    calendarEvents: calendarEventsReducer(prevState.calendarEvents, action),
    calendarEventVersions: calendarEventVersionsReducer(prevState.calendarEventVersions, action),
    schedulingLinks: schedulingLinksReducer(prevState.schedulingLinks, action),
    accounts: accountsReducer(prevState.accounts, action),
    vacationResponders: vacationRespondersReducer(prevState.vacationResponders, action),
    topics: topicsReducer(prevState.topics, action),
    localApplications: localApplicationsReducer(prevState.localApplications, action),
    apiTokens: apiTokensReducer(prevState.apiTokens, action),
  };

  if (isShallowEqual(prevState, nextState)) {
    return prevState;
  }

  return nextState;
}

export function mergeDataForBoot(
  state: DataState,
  newState: DataStateForBoot,
  deltaSince?: number,
): DataState {
  // Make sure this boot is newer than the previous boot.
  if (state.bootFetchedAt > newState.bootFetchedAt) {
    return state;
  }

  const isDelta = !isUndefined(deltaSince);

  // If this is a delta/diff boot, make sure the timestamps match.
  if (isDelta && state.bootFetchedAt !== deltaSince) {
    return state;
  }

  return {
    // Keep client-side data intact.
    ...state,
    // Prefer server-side boot data.
    ...newState,
    // Merge other transient values.
    contacts: {...state.contacts, ...newState.contacts},
    cards: {...state.cards, ...newState.cards},
    cardGroups: {...state.cardGroups, ...newState.cardGroups},
    cardNotes: {...state.cardNotes, ...newState.cardNotes},
    accounts: {...state.accounts, ...newState.accounts},
    // Boot only returns card custom fields but we may have other fields for accounts or teammates.
    // We want to keep all the fields that may not be returned from boot and update any from the boot call.
    customFields: {...state.customFields, ...newState.customFields},
    tags: {...state.tags, ...newState.tags},
    conversations: mergeResourceTables(state.conversations, newState.conversations),
    activities: {...state.activities, ...newState.activities},
    messages: {...state.messages, ...newState.messages},
    comments: mergeResourceTables(state.comments, newState.comments),
    customViews: {...state.customViews, ...newState.customViews},
    calendarEvents: {...state.calendarEvents, ...newState.calendarEvents},
    calendarEventVersions: {...state.calendarEventVersions, ...newState.calendarEventVersions},
    crmSyncs: state.crmSyncs,
    integrationTypes: state.integrationTypes,
    // Maybe merge updated resources from a delta/diff boot.
    teammates: isDelta ? {...state.teammates, ...newState.teammates} : newState.teammates,
    inboxes: isDelta ? mergeResourceTables(state.inboxes, newState.inboxes) : newState.inboxes,
    channels: isDelta ? mergeResourceTables(state.channels, newState.channels) : newState.channels,
    answers: isDelta ? mergeResourceTables(state.answers, newState.answers) : newState.answers,
    answerFolders: isDelta
      ? mergeResourceTables(state.answerFolders, newState.answerFolders)
      : newState.answerFolders,
    signatures: isDelta ? mergeResourceTables(state.signatures, newState.signatures) : newState.signatures,
    topics: state.topics,
    applications: state.applications,
    localApplications: state.localApplications,
    apiTokens: state.apiTokens,
  };
}
